"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@/context/AuthContext";
import { PAGE_ROUTES } from "@/constants/PermissionsConstants";
import isPermitted from "@/utils/isPermitted";
import { iframePagesApi } from "@/api/iframePages";
import { useTranslation } from "next-i18next";
import { logger } from "@/utils/logger";
import { useCoachEnabled } from "@/customHooks/useCoachEnabled";

export default function Sidebar() {
  const { t } = useTranslation("common");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openSubDropdown, setOpenSubDropdown] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [iframePages, setIframePages] = useState([]);
  const [isLoadingIframePages, setIsLoadingIframePages] = useState(false);
  const pathname = usePathname();
  const { user, allPermissions } = useAuth();
  // The coach is a per-client module: the nav entry needs the client flag on top
  // of the sidebar_coach permission.
  const { isModuleEnabled: isCoachEnabled, persona: coachPersona } = useCoachEnabled();
  // Fetch iframe pages on component mount
  useEffect(() => {
    const fetchIframePages = async () => {
      try {
        setIsLoadingIframePages(true);
        const response = await iframePagesApi.getAll();
        setIframePages(response.data);
      } catch (error) {
        logger.error("Failed to fetch iframe pages:", error);
        // Fallback to static PST item if API fails
      } finally {
        setIsLoadingIframePages(false);
      }
    };

    fetchIframePages();
  }, []);

  // Create dynamic iframe items from API data
  const dynamicIframeItems = useMemo(() => {
    return iframePages.map((page) => ({
      label: page.menu_label,
      key: page.menu_code,
      href: `/dynamic/${page.menu_code}`,
      permission: page.permission_label,
      icon: "/globe.svg",
    }));
  }, [iframePages]);

  // Single optimized DROPDOWN_ITEMS constant
  const DROPDOWN_ITEMS = useMemo(
    () => [
      {
        label: t("sidebar.dashboard"),
        href: "/dashboard",
        key: "dashboard",
        icon: "/sidebar/dashboard-icon.svg",
        permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.DASHBOARD,
      },
      // Coach assistant — only for clients that have the module. Conditionally
      // included (not `permission: null`) so it stays hidden everywhere else.
      ...(isCoachEnabled
        ? [
            {
              label: t("sidebar.coach", {
                defaultValue: "Your Coach",
                name: coachPersona.name,
              }),
              href: "/coach",
              key: "coach",
              icon: "/sidebar/coach.svg",
              permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.COACH,
            },
          ]
        : []),
      {
        label: t("sidebar.statistics"),
        href: "/stats",
        key: "stats",
        icon: "/sidebar/statistics.svg",
        permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.STATISTICS,
      },
      // Redesigned statistics page — super-admin only while under review.
      // Conditionally included (not `permission: null`), because the render
      // check treats `null == undefined` as "visible to everyone".
      ...(user?.sf_role?.has_all_permissions
        ? [
            {
              label: t("sidebar.statistics_1"),
              href: "/stats_1",
              key: "stats_1",
              icon: "/sidebar/statistics.svg",
              permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.STATISTICS,
            },
          ]
        : []),
      {
        label: t("sidebar.userProgress"),
        href: "/user-progress",
        key: "user-progress",
        icon: "/sidebar/user-progress.svg",
        permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.PROGRESS_USER,
      },
      {
        label: t("sidebar.eventProgress"),
        href: "/event-progress",
        key: "event-progress",
        icon: "/sidebar/group-progress.svg",
        permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.EVENT_PROGRESS,
      },
      {
        label: t("sidebar.conversations"),
        href: "/conversations",
        key: "conversations",
        icon: "/sidebar/conversation.svg",
        permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.CONVERSATION,
      },
      {
        label: t("sidebar.talks_library_tiles"),
        key: "talks-library-tiles",
        href: "/talks-library-tiles",
        image: "/sidebar/talks_icon_transparent.png",
        icon: "/sidebar/create-wizard-icon.svg",
        hasChildren: false,
        permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.TALKS_LIBRARY_TILES,
      },
      {
        label: t("sidebar.management"),
        items: [
          {
            label: t("sidebar.users"),
            key: "users",
            href: "/users",
            icon: "/sidebar/user-icon.svg",
            // items: [
            //   {
            //     label: t("sidebar.listing"),
            //     href: "/users",
            //     key: "user-listing",
            //   },
            //   {
            //     label: t("sidebar.createNew"),
            //     href: "/users/create",
            //     key: "user-create",
            //   },
            // ],
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.MANAGEMENT_USER,
          },
          {
            label: t("sidebar.events"),
            key: "groups",
            href: "/groups",
            icon: "/sidebar/groups.svg",
            // items: [
            //   {
            //     label: t("sidebar.listing"),
            //     href: "/groups",
            //     key: "groups-listing",
            //   },
            //   {
            //     label: t("sidebar.createNew"),
            //     href: "/groups/create",
            //     key: "groups-create",
            //   },
            // ],
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.MANAGEMENT_GROUPS,
          },
          {
            label: t("sidebar.clients"),
            key: "clients",
            href: "/clients",
            icon: "/sidebar/client-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.MANAGEMENT_CLIENTS,
          },
          {
            label: t("sidebar.roles"),
            key: "roles",
            href: "/roles",
            icon: "/sidebar/role.svg",
            // items: [
            //   {
            //     label: t("sidebar.listing"),
            //     href: "/roles",
            //     key: "role-listing",
            //   },
            //   {
            //     label: t("sidebar.createNew"),
            //     href: "/roles/create",
            //     key: "role-create",
            //   },
            // ],
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.MANAGEMENT_ROLE,
          },
          {
            label: t("sidebar.permissions"),
            key: "permissions",
            href: "/permissions",
            icon: "/sidebar/role.svg",
            hasChildren: false,
            // null means "super-admin-only" (checked through isPermitted with undefined permission).
            permission: null,
          },
          {
            label: t("sidebar.lti"),
            key: "lti",
            href: "/lti",
            icon: "/sidebar/client-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.MANAGEMENT_LTI,
          },
          {
            label: t("sidebar.news_management", "News"),
            key: "news-management",
            href: "/news-management",
            icon: "/sidebar/management-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.MANAGEMENT_NEWS,
          },
        ],
        key: "management",
        icon: "/sidebar/management-icon.svg",
        permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.MANAGEMENT,
      },
      {
        label: t("sidebar.content"),
        items: [
          {
            label: t("sidebar.talks_library"),
            key: "talks-library",
            href: "/talks-library",
            icon: "/sidebar/create-wizard-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.TALKS_LIBRARY,
          },
          {
            label: t("sidebar.report_templates", "Report Templates"),
            key: "report-templates",
            href: "/report-templates",
            icon: "/sidebar/create-wizard-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_REPORT_TEMPLATES,
          },
          {
            label: t("sidebar.reports", "Reports"),
            key: "report-management",
            href: "/report-management",
            icon: "/sidebar/create-wizard-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_REPORT_MANAGEMENT,
          },
        ],
        key: "content",
        icon: "/sidebar/content-icon.svg",
        permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.CONTENT,
      },
      {
        label: t("sidebar.maintenance"),
        items: [
          {
            label: t("sidebar.relations"),
            key: "relations-scenarios",
            href: "/maintenance/relations-scenarios",
            icon: "/sidebar/content-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_CONTENT_RELATIONS,
          },
          {
            label: t("relations.scenariosAndActors"),
            key: "relations-actors",
            href: "/maintenance/relations-actors",
            icon: "/sidebar/content-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_CONTENT_RELATIONS,
          },
          {
            label: t("sidebar.relationsLanguages"),
            key: "relations-languages",
            href: "/maintenance/relations-languages",
            icon: "/sidebar/content-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_CONTENT_RELATIONS,
          },
          // TALKS tools: super-admins, or anyone holding one of the tools that has its own
          // permission (Copy client, Settings overview). Conditionally included rather than
          // `permission: null`, because the render check treats `null == undefined` as
          // "visible to everyone".
          ...(user?.sf_role?.has_all_permissions ||
          [PAGE_ROUTES.TALK_TOOLS.COPY_CLIENT, PAGE_ROUTES.TALK_TOOLS.VIEW_SETTINGS].some((p) =>
            isPermitted(user, p, allPermissions),
          )
            ? [
                {
                  label: t("sidebar.talkTools"),
                  key: "talk-tools",
                  href: "/maintenance/talk-tools",
                  icon: "/sidebar/content-icon.svg",
                  hasChildren: false,
                },
              ]
            : []),
          // Conversation variants — super-admin only.
          ...(user?.sf_role?.has_all_permissions
            ? [
                {
                  label: t("sidebar.conversationVariants", "Conversation variants"),
                  key: "conversation-variants",
                  href: "/maintenance/variants",
                  icon: "/sidebar/content-icon.svg",
                  hasChildren: false,
                },
              ]
            : []),
          {
            label: t("sidebar.auditTrail"),
            key: "audit-trail",
            href: "/maintenance/audit-trail",
            icon: "/sidebar/content-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.MAINTENANCE_AUDIT_TRAIL,
          },
          {
            label: t("sidebar.aiIncidents"),
            key: "ai-incidents",
            href: "/maintenance/ai-incidents",
            icon: "/sidebar/content-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.MAINTENANCE_AI_INCIDENTS,
          },
          {
            label: t("sidebar.aiUsage"),
            key: "ai-usage",
            href: "/maintenance/ai-usage",
            icon: "/sidebar/content-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.MAINTENANCE_AI_USAGE,
          },
          {
            label: t("sidebar.tableMaintenance"),
            key: "table-maintenance",
            href: "/maintenance/tables",
            icon: "/sidebar/content-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_MAINTENANCE_TABLES,
          },
          {
            label: t("sidebar.actorBulkChange", "Actor Bulk Change"),
            key: "actor-bulk-change",
            href: "/maintenance/actor-bulk-change",
            icon: "/sidebar/content-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.ACTOR_BULK_CHANGE,
          },
          {
            label: t("sidebar.portalConfigs", "Portal Configs"),
            key: "portal-configs",
            href: "/maintenance/portal-configs",
            icon: "/sidebar/content-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.MANAGE_PORTAL_CONFIGS,
          },
          {
            label: t("sidebar.systemConfig", "System configuration"),
            key: "system-config",
            href: "/maintenance/system-config",
            icon: "/sidebar/content-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.MAINTENANCE_CRON,
          },
          {
            label: t("sidebar.coachSettings", "Coach settings"),
            key: "coach-settings",
            href: "/maintenance/coach-settings",
            icon: "/sidebar/content-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.MAINTENANCE_COACH,
          },
          {
            label: t("sidebar.readinessChecks", "Connection checks"),
            key: "readiness-checks",
            href: "/maintenance/readiness-checks",
            icon: "/sidebar/content-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.MAINTENANCE_READINESS_CHECKS,
          },
        ],
        key: "maintenance",
        icon: "/sidebar/management-icon.svg",
        permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.MAINTENANCE,
      },
      // Add dynamic iframe items as top-level menu items
      ...dynamicIframeItems,
      {
        label: t("sidebar.settings"),
        items: [
          {
            label: t("sidebar.preferences"),
            href: "/preference-settings",
            key: "preference-settings",
            icon: "/sidebar/preferences-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.PREFERENCES_SETTINGS,
          },
          {
            label: t("sidebar.account"),
            href: "/account-settings",
            key: "account-settings",
            icon: "/sidebar/user-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.ACCOUNT_SETTINGS,
          },
          {
            label: t("sidebar.privacy"),
            href: "/privacy-settings",
            key: "privacy-settings",
            icon: "/sidebar/privacy-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.PRIVACY_SETTINGS,
          },
          {
            label: t("sidebar.readinessCheck", "Connection check"),
            href: "/readiness-check",
            key: "readiness-check",
            icon: "/sidebar/preferences-icon.svg",
            hasChildren: false,
            permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.READINESS_CHECK,
          },
        ],
        key: "settings",
        icon: "/sidebar/account-settings-icon.svg",
        permission: PAGE_ROUTES.SIDEBAR_PERMISSIONS.SETTINGS,
      },
    ],
    [dynamicIframeItems, t, user, isCoachEnabled, coachPersona.name], // Add 't' to the dependency array
  );

  useEffect(() => {
    const segments = pathname.split("/").filter(Boolean);
    const firstString = segments[0];
    const secondString = segments[1];

    for (const dropdownItem of DROPDOWN_ITEMS) {
      if (dropdownItem.items == undefined) {
        if (dropdownItem.key === firstString) {
          setActiveDropdown(dropdownItem.key);
        }
      } else {
        if (dropdownItem.items.some((subItem) => subItem.key === firstString)) {
          setOpenDropdown(dropdownItem.key);
          setOpenSubDropdown(firstString);
          setActiveDropdown(firstString);
        } else if (
          dropdownItem.key === firstString &&
          secondString &&
          dropdownItem.items.some((subItem) => subItem.key === secondString)
        ) {
          setOpenDropdown(dropdownItem.key);
          setOpenSubDropdown(secondString);
          setActiveDropdown(secondString);
        }
      }
    }
  }, [pathname, DROPDOWN_ITEMS]);

  const toggleDropdown = (dropdown) => {
    if (openDropdown === dropdown) {
      setOpenDropdown(null);
      setOpenSubDropdown(null);
    } else {
      setOpenDropdown(dropdown);
      setOpenSubDropdown(null);
    }
  };
  const toggleSubDropdown = (subDropdown) => {
    if (openSubDropdown === subDropdown) {
      setOpenSubDropdown(null);
    } else {
      setOpenSubDropdown(subDropdown);
    }
  };

  const can_view_sidebar = useMemo(() => {
    return isPermitted(user, PAGE_ROUTES.SIDEBAR, allPermissions);
  }, [user, allPermissions]);

  const getMenuItemVisualSrc = (menuItem) => menuItem.image || menuItem.icon;

  return (
    <header
      className="h-dashboard-height bg-white shadow-custom py-11 px-8 flex-col justify-between w-full overflow-y-auto"
      style={{
        display: can_view_sidebar ? "flex" : "none",
      }}
    >
      <ul className="flex flex-col gap-3 items-start justify-start">
        {/* remove this hidden div when we have the dashboard page */}
        {DROPDOWN_ITEMS.map((dropdownItem) => (
          <li
            className="w-full"
            key={dropdownItem.key}
            style={{
              display:
                dropdownItem.permission == undefined ||
                isPermitted(user, dropdownItem.permission, allPermissions)
                  ? "block"
                  : "none",
            }}
          >
            {dropdownItem.items == undefined ? (
              <>
                <Link
                  href={dropdownItem.href}
                  className={`flex items-center gap-3.5 text-left justify-start px-3.5 py-3  hover:bg-nav-active-bg transition-all ease-in-out duration-300 w-full rounded-[6px] ${
                    activeDropdown === dropdownItem.key
                      ? "bg-nav-active-bg"
                      : "bg-white"
                  }`}
                  style={{
                    display:
                      dropdownItem.permission == undefined ||
                      isPermitted(user, dropdownItem.permission, allPermissions)
                        ? "flex"
                        : "none",
                  }}
                >
                  <Image
                    src={getMenuItemVisualSrc(dropdownItem)}
                    alt={dropdownItem.label}
                    width={18}
                    height={18}
                  />
                  <span className="text-gray-900 font-medium text-[14px] leading-[162%]">
                    {dropdownItem.label}
                  </span>
                </Link>
              </>
            ) : (
              <div className="w-full">
                <button
                  onClick={() => toggleDropdown(dropdownItem.key)}
                  className="flex items-center gap-3.5 text-left justify-between px-3.5 py-3 bg-white hover:bg-nav-active-bg transition-all ease-in-out duration-300 w-full rounded-[6px]"
                  style={{
                    display:
                      dropdownItem.permission == undefined ||
                      isPermitted(user, dropdownItem.permission, allPermissions)
                        ? "flex"
                        : "none",
                  }}
                >
                  <div className="flex items-center gap-3.5">
                    <Image
                      src={getMenuItemVisualSrc(dropdownItem)}
                      alt={dropdownItem.label}
                      width={18}
                      height={18}
                    />
                    <span className="text-gray-900 font-medium text-[14px] leading-[162%]">
                      {dropdownItem.label}
                    </span>
                  </div>
                  <Image
                    src="/sidebar/down-chevron-icon.svg"
                    alt={dropdownItem.label}
                    width={12}
                    height={6}
                    className={`transition-transform duration-300 ${
                      openDropdown === dropdownItem.key ? "rotate-180" : ""
                    }`}
                  />{" "}
                </button>
                {openDropdown === dropdownItem.key && (
                  <div className="pl-8 mt-2 flex flex-col gap-3 overflow-hidden transition-all duration-300 ease-in-out">
                    {dropdownItem.items.map((item) => (
                      <div key={item.key} className="w-full">
                        {item.hasChildren ? (
                          <button
                            type="button"
                            onClick={() => toggleSubDropdown(item.key)}
                            className={`flex items-center gap-3 text-left justify-between px-3.5 py-3 hover:bg-nav-active-bg
                           transition-all ease-in-out duration-300 rounded-[6px] w-full cursor-pointer ${
                             activeDropdown === item.key
                               ? "bg-nav-active-bg"
                               : "bg-white"
                           }`}
                            style={{
                              display:
                                item.permission === undefined ||
                                isPermitted(
                                  user,
                                  item.permission ?? undefined,
                                  allPermissions,
                                )
                                  ? "flex"
                                  : "none",
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <Image
                                src={item.icon}
                                alt={item.label}
                                width={16}
                                height={16}
                              />
                              <span className="text-gray-900 font-medium text-[14px] leading-[162%]">
                                {item.label}
                              </span>
                            </div>
                          </button>
                        ) : (
                          // Use real links for route changes so leave-guard can intercept unsaved exits.
                          <Link
                            href={item.href}
                            className={`flex items-center gap-3 text-left justify-between px-3.5 py-3 hover:bg-nav-active-bg
                           transition-all ease-in-out duration-300 rounded-[6px] w-full cursor-pointer no-underline ${
                             activeDropdown === item.key
                               ? "bg-nav-active-bg"
                               : "bg-white"
                           }`}
                            style={{
                              display:
                                item.permission === undefined ||
                                isPermitted(
                                  user,
                                  item.permission ?? undefined,
                                  allPermissions,
                                )
                                  ? "flex"
                                  : "none",
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <Image
                                src={item.icon}
                                alt={item.label}
                                width={16}
                                height={16}
                              />
                              <span className="text-gray-900 font-medium text-[14px] leading-[162%]">
                                {item.label}
                              </span>
                            </div>
                          </Link>
                        )}
                        {item.items != undefined &&
                          Array.isArray(item.items) && (
                            <div
                              className={`pl-8 flex-col gap-3  transition-all duration-300 ease-in-out ${
                                openSubDropdown === item.key
                                  ? "mt-2 flex"
                                  : "max-h-0 hidden"
                              }`}
                            >
                              {item.items.map((subItem) => (
                                <Link
                                  href={subItem.href}
                                  className={`flex items-center gap-3 text-left px-3.5 py-3  hover:bg-nav-active-bg transition-all ease-in-out duration-300 rounded-[6px] ${
                                    activeDropdown === subItem.key
                                      ? "bg-nav-active-bg"
                                      : "bg-white"
                                  }`}
                                  key={subItem.key}
                                  style={{
                                    display:
                                      subItem.permission == undefined ||
                                      isPermitted(
                                        user,
                                        subItem.permission,
                                        allPermissions,
                                      )
                                        ? "flex"
                                        : "none",
                                  }}
                                >
                                  {subItem.icon != undefined && (
                                    <Image
                                      src={subItem.icon}
                                      alt={subItem.label}
                                      width={16}
                                      height={16}
                                    />
                                  )}

                                  <span className="text-gray-900 font-medium text-[14px] leading-[162%]">
                                    {subItem.label}
                                  </span>
                                </Link>
                              ))}
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </header>
  );
}

{
  /* <li className="w-full">
<button
  onClick={() => toggleDropdown("sessions")}
  className="flex items-center gap-3.5 text-left justify-between px-3.5 py-3 bg-white hover:bg-nav-active-bg transition-all ease-in-out duration-300 w-full rounded-[6px]"
>
  <div className="flex items-center gap-3.5">
    <Image
      src="/sidebar/sessions-icon.svg"
      alt="Sessions"
      width={18}
      height={18}
    />
    <span className="text-gray-900 font-medium text-[14px] leading-[162%]">
      Sessions
    </span>
  </div>
  <Image
    src="/sidebar/down-chevron-icon.svg"
    alt="Sessions"
    width={12}
    height={6}
    className={`transition-transform duration-300 ${
      openDropdown === "sessions" ? "rotate-180" : ""
    }`}
  />
</button>
{openDropdown === "sessions" && (
  <div className="pl-8 mt-2 flex flex-col gap-3 overflow-hidden transition-all duration-300 ease-in-out max-h-96">
  </div>
)}
</li>
<li className="w-full">
<button
  onClick={() => toggleDropdown("conversations")}
  className="flex items-center gap-3.5 text-left justify-between px-3.5 py-3 bg-white hover:bg-nav-active-bg transition-all ease-in-out duration-300 w-full rounded-[6px]"
>
  <div className="flex items-center gap-3.5">
    <Image
      src="/sidebar/conversations-icon.svg"
      alt="Conversations"
      width={18}
      height={18}
    />
    <span className="text-gray-900 font-medium text-[14px] leading-[162%]">
      Conversations
    </span>
  </div>
  <Image
    src="/sidebar/down-chevron-icon.svg"
    alt="Conversations"
    width={12}
    height={6}
    className={`transition-transform duration-300 ${
      openDropdown === "conversations" ? "rotate-180" : ""
    }`}
  />
</button>
{openDropdown === "conversations" && (
  <div className="pl-8 mt-2 flex flex-col gap-3 overflow-hidden transition-all duration-300 ease-in-out max-h-96">
  </div>
)}
</li>
<li className="w-full">
<button
  onClick={() => toggleDropdown("reports")}
  className="flex items-center gap-3.5 text-left justify-between px-3.5 py-3 bg-white hover:bg-nav-active-bg transition-all ease-in-out duration-300 w-full rounded-[6px]"
>
  <div className="flex items-center gap-3.5">
    <Image
      src="/sidebar/reports-icon.svg"
      alt="Reports"
      width={18}
      height={18}
    />
    <span className="text-gray-900 font-medium text-[14px] leading-[162%]">
      Reports
    </span>
  </div>
  <Image
    src="/sidebar/down-chevron-icon.svg"
    alt="Reports"
    width={12}
    height={6}
    className={`transition-transform duration-300 ${
      openDropdown === "reports" ? "rotate-180" : ""
    }`}
  />
</button>
{openDropdown === "reports" && (
  <div className="pl-8 mt-2 flex flex-col gap-3 overflow-hidden transition-all duration-300 ease-in-out max-h-96">
  </div>
)}
</li> */
}
